import React, { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';

import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useIntl } from 'react-intl';
import Modal from '../common/Modal';
import CardSelect from '../common/CardSelect';
import styles from './AddressValidatorModal.module.scss';
import messages from './messages';

import { mapCountry } from '../../utils';

const AddressValidatorModal = ({
  firstName,
  lastName,
  currentAddress,
  verifiedAddress,
  canVerify,
  onSubmit,
}) => {
  const isLoading = useSelector((state) => state.cart.status === 'loading');

  const { formatMessage } = useIntl();
  const [addressSelection, setAddressSelection] = useState(
    canVerify ? 'verified' : 'current',
  );

  const handleSubmit = useCallback(() => {
    onSubmit(
      addressSelection === 'verified' ? verifiedAddress : currentAddress,
      true,
    );
  }, [addressSelection, verifiedAddress, currentAddress, onSubmit]);

  const handleEdit = useCallback(() => {
    if (!isLoading) {
      onSubmit(currentAddress, false);
    }
  }, [isLoading, currentAddress, onSubmit]);

  const renderAddress = useCallback(
    (address) => (
      <div className={classnames(styles.address, 'p4')} data-private>
        <span>{`${firstName} ${lastName}`}</span>
        <span>{address.streetAddress}</span>
        {!!address?.additionalStreetInfo && (
          <span>{address?.additionalStreetInfo}</span>
        )}
        <span>{`${address.city}, ${address.state} ${address.postalCode}`}</span>
        <span>{mapCountry(address.country)}</span>
      </div>
    ),
    [firstName, lastName],
  );

  return (
    <Modal isVisible onClose={handleEdit}>
      <div className={styles.container}>
        <div className={styles.wrapper}>
          <div
            className={styles['btn-close']}
            onClick={handleEdit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleEdit();
              }
            }}
            role="button"
            aria-label={formatMessage(messages.closeModal)}
            tabIndex="0"
          />
          <h2 className={styles.title}>{formatMessage(messages.title)}</h2>
          <span>
            {formatMessage(messages[canVerify ? 'subtitle1' : 'subtitle2'])}
          </span>
          <span className={styles['address-selectors-wrapper']}>
            {canVerify && (
              <CardSelect
                title={formatMessage(messages.verifiedAddress)}
                id="verified-selection"
                value="verified"
                name="selection"
                isSelected={addressSelection === 'verified'}
                onChange={setAddressSelection}
              >
                {renderAddress(verifiedAddress)}
              </CardSelect>
            )}
            <CardSelect
              title={formatMessage(messages.addressYouEntered)}
              id="current-selection"
              value="current"
              name="selection"
              isSelected={addressSelection === 'current'}
              onChange={canVerify ? setAddressSelection : undefined}
            >
              {renderAddress(currentAddress)}
            </CardSelect>
          </span>
        </div>
        <div className={styles.action}>
          <button
            className="btn"
            type="submit"
            disabled={isLoading}
            onClick={handleSubmit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSubmit();
              }
            }}
            tabIndex={0}
          >
            {formatMessage(
              messages[canVerify ? 'useAddress' : 'confirmAddress'],
            )}
          </button>
          <span
            className={styles.edit}
            onClick={handleEdit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleEdit();
              }
            }}
            role="button"
            tabIndex={0}
          >
            {formatMessage(messages[canVerify ? 'editManually' : 'edit'])}
          </span>
        </div>
      </div>
    </Modal>
  );
};

AddressValidatorModal.propTypes = {
  firstName: PropTypes.string.isRequired,
  lastName: PropTypes.string.isRequired,
  currentAddress: PropTypes.shape({
    streetAddress: PropTypes.string.isRequired,
    city: PropTypes.string.isRequired,
    postalCode: PropTypes.string.isRequired,
    country: PropTypes.string.isRequired,
    state: PropTypes.string.isRequired,
    additionalStreetInfo: PropTypes.string.isRequired,
  }).isRequired,
  verifiedAddress: PropTypes.shape({
    streetAddress: PropTypes.string,
    city: PropTypes.string,
    postalCode: PropTypes.string,
    country: PropTypes.string,
    state: PropTypes.string,
  }),
  canVerify: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

AddressValidatorModal.defaultProps = {
  verifiedAddress: undefined,
};

export default AddressValidatorModal;
